/**
 * A screen's own stylesheet, loaded when the screen is first wanted.
 *
 * app.css holds what the chrome needs before any screen is up; each screen's
 * look lives in `src/styles/screens/<screen>.css`, which the build publishes
 * under assets/ with a content hash and names in PLAYGROUND_SHEETS (JSON
 * text). The sheet is a `<link>` to this origin, so the CSP's `style-src
 * 'self'` admits it and nothing inline is needed. A screen waits for its sheet
 * at most `SHEET_WAIT_MS`: a slow or missing sheet never keeps it from
 * opening.
 */

declare const PLAYGROUND_SHEETS: string | undefined;

export const SHEET_WAIT_MS = 1500;

/** Where each screen's sheet is published, by screen name ({} when the build named none, as under test). */
export const sheetMap = (raw: string | undefined): Readonly<Record<string, string>> => {
  if (typeof raw !== 'string') return {};
  try {
    const map = JSON.parse(raw) as unknown;
    if (!map || typeof map !== 'object' || Array.isArray(map)) return {};
    return Object.fromEntries(Object.entries(map as Record<string, unknown>)
      .filter((e): e is [string, string] => typeof e[1] === 'string' && e[1].startsWith('/assets/') && e[1].endsWith('.css')));
  } catch {
    return {};
  }
};

const MAP = sheetMap(typeof PLAYGROUND_SHEETS === 'string' ? PLAYGROUND_SHEETS : undefined);
const loading = new Map<string, Promise<void>>();

/** Load screen `name`'s sheet once; resolves when it has loaded, failed, or kept the screen waiting long enough. */
export const sheetFor = (name: string, doc: Document | undefined = typeof document === 'undefined' ? undefined : document): Promise<void> => {
  const href = MAP[name];
  if (!href || !doc) return Promise.resolve();
  const had = loading.get(href);
  if (had) return had;
  const done = new Promise<void>((resolve) => {
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.screen = name;
    const timer = setTimeout(resolve, SHEET_WAIT_MS);
    const settle = (): void => { clearTimeout(timer); resolve(); };
    link.addEventListener('load', settle, { once: true });
    link.addEventListener('error', settle, { once: true });
    doc.head.append(link);
  });
  loading.set(href, done);
  return done;
};
